import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal, Type} from '@angular/core';
import {ChangesService} from '@common/shared/services/changes.service';
import {Store} from '@ngrx/store';
import {selectActiveWorkspace, selectCurrentUser, selectIsAdmin} from '../../core/reducers/users-reducer';
import {logout} from '../../core/actions/users.actions';
import {setAutoRefresh} from '../../core/actions/layout.actions';
import {MatDialog} from '@angular/material/dialog';
import {ConfigurationService} from '../../shared/services/configuration.service';
import {
  GetCurrentUserResponseUserObjectCompany
} from '~/business-logic/model/users/getCurrentUserResponseUserObjectCompany';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs/operators';
import {selectRouterUrl} from '../../core/reducers/router-reducer';
import {TipsService} from '../../shared/services/tips.service';
import {WelcomeMessageComponent} from '../welcome-message/welcome-message.component';
import {ActivatedRoute, NavigationEnd, Router, RouterLink} from '@angular/router';
import {LoginService} from '~/shared/services/login.service';
import {selectUserSettingsNotificationPath} from '~/core/reducers/view.reducer';
import {selectInvitesPending} from '~/core/reducers/users.reducer';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {selectDarkTheme, selectForcedTheme, selectHeaderMenu} from '@common/core/reducers/view.reducer';
import {AppearanceComponent} from '../appearance/appearance.component';
import {BreadcrumbsComponent} from '@common/layout/breadcrumbs/breadcrumbs.component';
import {RefreshButtonComponent} from '@common/shared/components/refresh-button/refresh-button.component';
import {HeaderUserMenuActionsComponent} from '~/layout/header/header-user-menu-actions/header-user-menu-actions.component';
import {ShowOnlyUserWorkComponent} from '@common/shared/components/show-only-user-work/show-only-user-work.component';
import {HeaderNavbarTabsComponent} from '@common/layout/header-navbar-tabs/header-navbar-tabs.component';
import {MatIconModule} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {NgOptimizedImage} from '@angular/common';
import {TooltipDirective} from '@common/shared/ui-components/indicators/tooltip/tooltip.directive';
import {MatIconButton} from '@angular/material/button';

@Component({
  selector: 'sm-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'globalSearchTrigger($event)'
  },
  imports: [
    BreadcrumbsComponent,
    RefreshButtonComponent,
    HeaderUserMenuActionsComponent,
    ShowOnlyUserWorkComponent,
    HeaderNavbarTabsComponent,
    MatIconModule,
    MatMenu,
    RouterLink,
    MatMenuItem,
    MatMenuTrigger,
    NgOptimizedImage,
    TooltipDirective,
    MatIconButton
  ]
})
export class HeaderComponent implements OnInit {
  private store = inject(Store);
  private dialog = inject(MatDialog);
  public tipsService = inject(TipsService);
  public changesService = inject(ChangesService);
  private loginService = inject(LoginService);
  private router = inject(Router);
  private activeRoute = inject(ActivatedRoute);
  private configService = inject(ConfigurationService);
  private searchComponentPromise: Promise<Type<unknown>>;

  isShareMode = input<boolean>();
  isLogin = input<boolean>();
  hideMenus = input<boolean>();

  protected environment = this.configService.configuration;
  protected url = this.store.selectSignal(selectRouterUrl);
  protected user = this.store.selectSignal(selectCurrentUser);
  protected isAdmin = this.store.selectSignal(selectIsAdmin);
  protected userNotificationPath = this.store.selectSignal(selectUserSettingsNotificationPath);
  protected invitesPending = this.store.selectSignal(selectInvitesPending);
  protected darkTheme = this.store.selectSignal(selectDarkTheme);
  protected forcedTheme = this.store.selectSignal(selectForcedTheme);
  protected userFocus = signal<boolean>(false);
  protected hideSideNav = signal<boolean>(false);
  protected showAutoRefresh = signal<boolean>(false);
  protected searchActive = signal(false);
  public activeWorkspace = toSignal<GetCurrentUserResponseUserObjectCompany>(this.store.select(selectActiveWorkspace)
    .pipe(
      filter(workspace => !!workspace),
      distinctUntilChanged()
    )
  );

  protected contextNavbar = this.store.selectSignal(selectHeaderMenu);
    contextNavbarLength = computed(() => {
    return this.contextNavbar()?.length;
  });

  // Ctrl + K -> Global Search Dialog
  globalSearchTrigger(e: KeyboardEvent): void {
    const isModifierPressed = e.metaKey || e.ctrlKey;
    const isKPressed = e.key.toLowerCase() === 'k';
    if (isModifierPressed && isKPressed && this.dialog.openDialogs.length === 0) {
      e.preventDefault();
      e.stopPropagation();
      document.body.querySelectorAll('.cdk-overlay-backdrop').forEach((c: HTMLDivElement) => c.click());
      document.body.querySelectorAll('.cdk-overlay-container').forEach((c: HTMLDivElement) => c.click());
      this.openGlobalSearch();
    }
  }

  openGlobalSearchIfNeeded() {
    if (this.dialog.openDialogs.length === 0 && (this.activeRoute.snapshot.queryParams.gq || this.activeRoute.snapshot.queryParams.tab)) {
      this.openGlobalSearch();
    }
    this.getRouteData();
  }

  constructor() {
    this.getRouteData();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(100),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.openGlobalSearchIfNeeded();
      });
  }

  ngOnInit(): void {
    this.openGlobalSearchIfNeeded();
  }

  getRouteData() {
    this.userFocus.set(!!this.activeRoute?.firstChild?.snapshot.data?.userFocus);
    this.hideSideNav.set(this.activeRoute?.firstChild?.snapshot.data.hideSideNav);
    let active = false;
    let last = this.activeRoute;
    while (last.firstChild) {
      if (last.snapshot.data.search !== undefined) {
        active = last.snapshot.data.search;
      }
      last = last.firstChild;
    }
    this.showAutoRefresh.set(last.snapshot.data.showAutoRefresh);
    this.searchActive.set(active || last.snapshot.data.search);
  }

  logout() {
    this.loginService.clearLoginCache();
    this.store.dispatch(logout({}));
  }

  openWelcome(event: MouseEvent) {
    event.preventDefault();
    this.dialog.open(WelcomeMessageComponent, {data: {step: 2}, panelClass: 'dialog-md'});
  }

  openAppearance(event: MouseEvent) {
    event.preventDefault();
    this.dialog.open(AppearanceComponent);
  }

  toggleAutoRefresh(autoRefresh: boolean) {
    this.store.dispatch(setAutoRefresh({autoRefresh}));
  }

  openGlobalSearch() {
    if (!this.searchComponentPromise) {
      this.searchComponentPromise = import(
        '@common/dashboard-search/global-search-dialog/global-search-dialog.component'
        ).then(({GlobalSearchDialogComponent}) => GlobalSearchDialogComponent);
    }

    this.searchComponentPromise.then(component => {
      this.dialog.open(component, {
        width: '920px',
        enterAnimationDuration: 0
      });
    });
  }
}
